import React, { useEffect } from "react";
import CustomDialog from "../../components/layouts/common/CustomDialog";
import CustomInput from "../../components/inputs/CustomInputs";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import {
  Autocomplete,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItem,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { LOG_TYPE, MATERIAL_TYPE } from "../../utils/constants";
import { findObjectKeyByValue, titleCase } from "../../utils/main";
import moment from "moment";
import AsyncDropDown from "../../components/inputs/AsyncDropDown";
import { StyledSearchBar } from "../../components/inputs/SearchBar";
import { FetchvendorRawMaterialWiseApi } from "../../apis/vendor.api";
import { closeModal, openModal } from "../../store/actions/modalAction";
import { useDispatch } from "react-redux";
import { QrCodeScanner } from "@mui/icons-material";
import QrScannerPopup from "../../components/Scanner/QrScannerPopup";
import { CenteredBox } from "../../components/layouts/OneViewBox";
import {
  fetchProductSubCategoryApi,
  getProductSubCategoryByIdApi,
} from "../../apis/productSubCategory.api";
import { fetchlocationMasterApi } from "../../apis/locationMaster.api";
import PaddingBoxInDesktop from "../../components/layouts/PaddingBoxDesktop";

const isValidObjectId = (id) => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};

const InventoryLogsCreateui = ({
  id,
  QrId,
  QrLoading,
  SetQrId,
  loading,
  onSubmit,
  fields,
  setFields,
  APICall,
}) => {
  const dispatch = useDispatch();
  const materialtype = [
    ...Object.keys(MATERIAL_TYPE)?.map((key) => ({
      label: titleCase(key).toUpperCase(),
      _id: MATERIAL_TYPE[key],
    })),
  ];

  const onScan = (data) => {
    SetQrId(data.text);
  };

  const onQrBtnClick = () => {
    dispatch(
      openModal(
        <QrScannerPopup
          onScan={onScan}
          onClose={() => dispatch(closeModal("scanner"))}
        />,
        "sm",
        false,
        "scanner",
      ),
    );
  };

  const updateParamsWithTotals = (paramMap) => {
    setFields({
      ...fields,
      iqc: {
        ...fields.iqc,
        parameters: paramMap,
      },
    });
  };

  useEffect(() => {
    if (id && fields.productSubCategoryId && !fields.iqc?.parameters) {
      const fetchParams = async () => {
        try {
          const response = await getProductSubCategoryByIdApi({
            id: fields.productSubCategoryId,
          });
          const params = response.data?.parameters || [];
          const paramMap = params.reduce((acc, name) => {
            acc[name] = { pass: 0, reworkable: 0, rejected: 0 };
            return acc;
          }, {});
          setFields((prev) => ({
            ...prev,
            iqc: {
              ...prev.iqc,
              parameters: paramMap,
            },
          }));
        } catch (error) {
          console.error("Error fetching parameters:", error);
        }
      };
      fetchParams();
    }
  }, [id, fields.productSubCategoryId, fields.iqc, setFields]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (id) {
      const quantityVal = Number(fields?.quantity) || 0;
      const totalIqcQty =
        (Number(fields?.iqc?.accepted) || 0) +
        (Number(fields?.iqc?.reworkable) || 0) +
        (Number(fields?.iqc?.rejected) || 0);

      if (totalIqcQty !== quantityVal) {
        setFields({
          ...fields,
          err: "IQC totals must be exactly same as quantity",
        });
        return;
      }

      const paramEntries = Object.entries(fields?.iqc?.parameters || {});
      for (const [name, values] of paramEntries) {
        const rowTotal =
          (Number(values?.pass) || 0) +
          (Number(values?.reworkable) || 0) +
          (Number(values?.rejected) || 0);
        if (rowTotal !== quantityVal) {
          setFields({
            ...fields,
            err: `IQC totals for ${name} must equal quantity`,
          });
          return;
        }
      }
    }

    if (QrId === "" || id || QrLoading.current) {
      QrLoading.current = true;
      return onSubmit(e);
    } else if (isValidObjectId(QrId) === false) {
      setFields({ ...fields, err: "Please enter a valid QR Code ID" });
    }
  };

  return (
    <CustomDialog
      id={id ? "update" : "inventoryLog"}
      loading={loading}
      err={fields.err}
      onSubmit={handleSubmit}
      title={"Update GRN"}
      closeText={"Close"}
      confirmText={"Update"}
    >
      {loading ? (
        <CenteredBox>
          <CircularProgress />
        </CenteredBox>
      ) : (
        <>
          {!id ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 3,
                  width: "100%",
                }}
              >
                <FormControl color="primary" fullWidth variant="outlined">
                  <InputLabel
                    color="primary"
                    htmlFor="outlined-adornment-boxid"
                  >
                    Enter QR ID
                  </InputLabel>
                  <OutlinedInput
                    id="outlined-adornment-boxid"
                    value={QrId || ""}
                    onChange={(e) => SetQrId(e.target.value)}
                    type="text"
                    autoFocus={true}
                    disabled={loading}
                    endAdornment={
                      <>
                        {loading && <CircularProgress size={30} />}
                        <InputAdornment position="end">
                          <IconButton onClick={onQrBtnClick} edge="end">
                            <QrCodeScanner />
                          </IconButton>
                        </InputAdornment>
                      </>
                    }
                    color="primary"
                    label="Enter QR ID"
                  />
                </FormControl>
              </Box>
              <Box
                width={"100%"}
                gap={2}
                display={"flex"}
                flexDirection={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mt: 1,
                    width: "50%",
                  }}
                >
                  <DesktopDatePicker
                    disabled={loading || id}
                    inputFormat="DD-MM-yyyy"
                    value={moment(fields?.date ?? null)}
                    onChange={(changedVal) => {
                      setFields({ ...fields, err: "", date: changedVal });
                    }}
                    renderInput={(props) => (
                      <CustomInput {...props} sx={{ height: "56px" }} />
                    )}
                    type="date"
                    label={"Date*"}
                  />
                </Box>

                <Box mt={1} sx={{ width: "50%" }}>
                  <Autocomplete
                    disabled={loading || id}
                    options={materialtype}
                    value={findObjectKeyByValue(
                      fields?.materialType,
                      MATERIAL_TYPE,
                    )?.toUpperCase()}
                    onChange={(e, newVal) => {
                      setFields({
                        ...fields,
                        materialType: newVal ? newVal._id : null,
                      });
                    }}
                    sx={{
                      width: "100%",
                      display: "flex",
                      "*": { display: "flex", justifyContent: "center" },
                    }}
                    renderInput={(params) => (
                      <CustomInput
                        placeholder="Select Material Type*"
                        {...params}
                        label="Select Material Type*"
                        margin="dense"
                      />
                    )}
                  />
                </Box>
              </Box>

              <Box mt={1}>
                {fields?.materialType && (
                  <AsyncDropDown
                    key={fields.materialType}
                    Value={
                      fields?.materialName
                        ? {
                            _id: fields?.materialIds?._id,
                            name: fields?.materialName,
                          }
                        : null
                    }
                    lazyFun={async (para) => {
                      const apiFn = APICall(fields.materialType);
                      return apiFn ? await apiFn.api(para) : [];
                    }}
                    OptionComponent={({ option, ...rest }) => {
                      return (
                        <ListItem {...rest}>
                          {option?.name} {option?.mpn && `(${option?.mpn})`}
                        </ListItem>
                      );
                    }}
                    onChange={async (changedVal) => {
                      setFields({
                        ...fields,
                        materialId: changedVal ? changedVal?._id : null,
                        itemCode: changedVal
                          ? APICall(fields.materialType, changedVal)?.code || ""
                          : "",
                        pricePerUnits: changedVal
                          ? changedVal?.price_per_unit
                          : "",
                        amount: changedVal
                          ? (changedVal?.price_per_unit || 0) *
                            (Number(fields?.quantity) || 0)
                          : 0,
                        materialName:
                          changedVal && changedVal?.mpn
                            ? `${changedVal?.name ?? ""} (${changedVal?.mpn ?? ""})`.trim()
                            : null,
                      });
                    }}
                    titleKey={"name"}
                    valueKey={"_id"}
                    InputComponent={(params) => {
                      const label = APICall(fields.materialType)?.label || "";
                      return (
                        <CustomInput
                          {...params}
                          label={`${label}*`}
                          placeholder={`${label}*`}
                          margin="dense"
                          InputLabelProps={{ shrink: true }}
                        />
                      );
                    }}
                  />
                )}
              </Box>
              {!id && (
                <Box mt={1}>
                  <CustomInput
                    disabled={loading}
                    value={fields.itemCode}
                    onChange={(e) => {
                      setFields({
                        ...fields,
                        err: "",
                        itemCode: e.target.value,
                      });
                    }}
                    type="text"
                    label={"Item Code*"}
                  />
                </Box>
              )}
              {!id &&
                // fields?.materialType === 1 &&
                fields?.materialType !== MATERIAL_TYPE.FG &&
                fields.logType === LOG_TYPE.In && (
                  <Box>
                    <AsyncDropDown
                      key={fields?.materialId}
                      defaultVal={
                        fields.vendorId
                          ? {
                              _id: fields?.vendorId?._id
                                ? fields?.vendorId?._id
                                : fields?.vendorId,
                              name: fields?.vendorId?.name
                                ? fields?.vendorId?.name
                                : fields?.vendorName,
                            }
                          : null
                      }
                      lazyFun={async (para) =>
                        await FetchvendorRawMaterialWiseApi({
                          ...para,
                          allStatus: true,
                          materialId: fields?.materialId,
                        })
                      }
                      OptionComponent={({ option, ...rest }) => {
                        return <ListItem {...rest}>{option.name}</ListItem>;
                      }}
                      onChange={async (changedVal) => {
                        setFields({
                          ...fields,
                          vendorId: changedVal ? changedVal._id : null,
                          vendorName: changedVal ? changedVal.name : null,
                        });
                      }}
                      titleKey={"name"}
                      valueKey={"_id"}
                      InputComponent={(params) => (
                        <CustomInput
                          {...params}
                          label="Select Vendor"
                          placeholder="Select Vendor"
                          margin="dense"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </Box>
                )}
              {/* {!id && (
                <Box mt={1}>
                  <CustomInput
                    disabled={loading}
                    value={fields.batchNumber}
                    onChange={(e) => {
                      setFields({
                        ...fields,
                        err: "",
                        batchNumber: e.target.value,
                      });
                    }}
                    type="text"
                    label={"Batch Number*"}
                  />
                </Box>
              )} */}

              {/* <Box mt={1}>
                <Autocomplete
                  disabled={true}
                  options={logType}
                  value={findObjectKeyByValue(fields?.logType, LOG_TYPE)}
                  onChange={(e, newVal) => {
                    setFields({
                      ...fields,
                      logType: newVal ? newVal._id : null,
                    });
                  }}
                  sx={{
                    width: "100%",
                    display: "flex",
                    "*": { display: "flex", justifyContent: "center" },
                  }}
                  renderInput={(params) => (
                    <CustomInput
                      placeholder="Select Log Type*"
                      {...params}
                      label="Select Log Type*"
                      margin="dense"
                    />
                  )}
                />
              </Box> */}

              <CustomInput
                disabled={loading || id}
                value={fields?.quantity}
                onChange={(e) => {
                  const newQuantity = Number(e.target.value) || 0;
                  setFields((prevFields) => ({
                    ...prevFields,
                    quantity: newQuantity,
                  }));
                }}
                type="number"
                label={"Quantity*"}
                onWheel={(e) => e.target.blur()}
              />

              {!id && (
                <CustomInput
                  disabled={loading}
                  value={fields?.amount}
                  onChange={(e) =>
                    setFields({ ...fields, err: "", amount: e.target.value })
                  }
                  type="text"
                  label={"Amount"}
                />
              )}
              <Box mt={1}>
                <CustomInput
                  disabled={loading}
                  value={fields.invoiceNumber}
                  onChange={(e) => {
                    setFields({
                      ...fields,
                      err: "",
                      invoiceNumber: e.target.value,
                    });
                  }}
                  type="text"
                  label={"Invoice No"}
                />
              </Box>
            </>
          ) : (
            <>
              <Box mt={2}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <tbody>
                    {/* Render two label-value pairs per row */}
                    <tr>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                          width: "15%",
                        }}
                      >
                        Date
                      </td>
                      <td
                        style={{
                          padding: 8,
                          border: "1px solid #eee",
                          width: "35%",
                        }}
                      >
                        {fields?.date
                          ? moment(fields?.date).format("DD-MM-YYYY")
                          : ""}
                      </td>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                          width: "15%",
                        }}
                      >
                        Material Type
                      </td>
                      <td
                        style={{
                          padding: 8,
                          border: "1px solid #eee",
                          width: "35%",
                        }}
                      >
                        {titleCase(
                          findObjectKeyByValue(
                            fields?.materialType,
                            MATERIAL_TYPE,
                          ) || "",
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                        }}
                      >
                        Material Name
                      </td>
                      <td style={{ padding: 8, border: "1px solid #eee" }}>
                        {fields?.materialIds?.name || ""}
                      </td>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                        }}
                      >
                        Log Type
                      </td>
                      <td style={{ padding: 8, border: "1px solid #eee" }}>
                        {titleCase(
                          findObjectKeyByValue(fields?.logType, LOG_TYPE) || "",
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                        }}
                      >
                        Quantity
                      </td>
                      <td style={{ padding: 8, border: "1px solid #eee" }}>
                        <CustomInput
                          disabled={loading}
                          value={fields?.quantity}
                          min={0}
                          onChange={(e) => {
                            const newQuantity = Number(e.target.value) || 0;
                            setFields({
                              ...fields,
                              quantity: newQuantity,
                            });
                          }}
                          type="number"
                          label={"Quantity*"}
                          onWheel={(e) => e.target.blur()}
                        />
                      </td>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                        }}
                      >
                        Invoice No
                      </td>
                      <td style={{ padding: 8, border: "1px solid #eee" }}>
                        <CustomInput
                          disabled={loading}
                          value={fields?.invoiceNumber || ""}
                          onChange={(e) => {
                            setFields({
                              ...fields,
                              invoiceNumber: e.target.value,
                            });
                          }}
                          type="text"
                          label={"Invoice No"}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                        }}
                      >
                        Amount
                      </td>
                      <td style={{ padding: 8, border: "1px solid #eee" }}>
                        {fields?.amount}
                      </td>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                        }}
                      >
                        Vendor
                      </td>
                      <td style={{ padding: 8, border: "1px solid #eee" }}>
                        {fields?.vendorName || fields?.vendorId?.name || ""}
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                        }}
                      >
                        Item Code
                      </td>
                      <td style={{ padding: 8, border: "1px solid #eee" }}>
                        {fields?.materialIds?.itemCode}
                      </td>
                      <td
                        style={{
                          fontWeight: "bold",
                          padding: 8,
                          border: "1px solid #eee",
                        }}
                      >
                        Product Sub Category
                      </td>
                      <td style={{ padding: 8, border: "1px solid #eee" }}>
                        <AsyncDropDown
                          lazyFun={async (para) =>
                            await fetchProductSubCategoryApi({
                              ...para,
                              allStatus: true,
                            })
                          }
                          OptionComponent={({ option, ...rest }) => {
                            return <ListItem {...rest}>{option.name}</ListItem>;
                          }}
                          value={fields?.productSubCategoryId}
                          onChange={async (changedVal) => {
                            if (!changedVal) {
                              setFields({
                                ...fields,
                                productSubCategoryId: null,
                                iqc: {
                                  ...fields.iqc,
                                  parameters: {},
                                  accepted: 0,
                                  rejected: 0,
                                  reworkable: 0,
                                },
                              });
                              return;
                            }
                            try {
                              const response =
                                await getProductSubCategoryByIdApi({
                                  id: changedVal._id,
                                });
                              const params = response.data?.parameters || [];
                              const paramMap = params.reduce((acc, name) => {
                                acc[name] = {
                                  pass: 0,
                                  reworkable: 0,
                                  rejected: 0,
                                };
                                return acc;
                              }, {});
                              setFields({
                                ...fields,
                                productSubCategoryId: changedVal._id,
                                iqc: {
                                  ...fields.iqc,
                                  parameters: paramMap,
                                  accepted: 0,
                                  rejected: 0,
                                  reworkable: 0,
                                },
                              });
                            } catch (error) {
                              console.error(
                                "Error fetching parameters:",
                                error,
                              );
                            }
                          }}
                          titleKey={"name"}
                          valueKey={"_id"}
                          InputComponent={(params) => (
                            <StyledSearchBar
                              placeholder={"Select Product Sub Category*"}
                              {...params}
                              margin="dense"
                            />
                          )}
                        />
                      </td>
                    </tr>
                    <tr>
                      {" "}
                      <td colSpan={4} style={{ padding: 0, border: "none" }}>
                        {
                          <PaddingBoxInDesktop
                            mt={3}
                            sx={{
                              display: "flex",
                              flex: 1,
                              justifyContent: "flex-end",
                            }}
                          >
                            <AsyncDropDown
                              lazyFun={async (para) =>
                                await fetchlocationMasterApi({
                                  ...para,
                                  allStatus: true,
                                })
                              }
                              OptionComponent={({ option, ...rest }) => {
                                return (
                                  <ListItem {...rest}>{option.name}</ListItem>
                                );
                              }}
                              value={fields?.product_name}
                              onChange={async (changedVal) => {
                                setFields({
                                  ...fields,
                                  location: changedVal ? changedVal._id : null,
                                });
                              }}
                              titleKey={"name"}
                              valueKey={"_id"}
                              InputComponent={(params) => (
                                <StyledSearchBar
                                  placeholder={"Location Master*"}
                                  {...params}
                                  margin="none"
                                />
                              )}
                            />
                          </PaddingBoxInDesktop>
                        }
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={4} style={{ padding: 0, border: "none" }}>
                        <Box mt={2} mb={2}>
                          <Typography variant="h6" mb={2}>
                            IQC Totals
                          </Typography>
                          <Box display="flex" gap={2}>
                            <CustomInput
                              disabled={loading}
                              value={fields?.iqc?.accepted ?? 0}
                              onChange={(e) => {
                                setFields({
                                  ...fields,
                                  iqc: {
                                    ...fields.iqc,
                                    accepted: Number(e.target.value) || 0,
                                  },
                                });
                              }}
                              type="number"
                              label={"Passed"}
                              min={0}
                              onWheel={(e) => e.target.blur()}
                            />
                            <CustomInput
                              disabled={loading}
                              value={fields?.iqc?.reworkable ?? 0}
                              onChange={(e) => {
                                setFields({
                                  ...fields,
                                  iqc: {
                                    ...fields.iqc,
                                    reworkable: Number(e.target.value) || 0,
                                  },
                                });
                              }}
                              type="number"
                              label={"Reworkable"}
                              min={0}
                              onWheel={(e) => e.target.blur()}
                            />
                            <CustomInput
                              disabled={loading}
                              value={fields?.iqc?.rejected ?? 0}
                              onChange={(e) => {
                                setFields({
                                  ...fields,
                                  iqc: {
                                    ...fields.iqc,
                                    rejected: Number(e.target.value) || 0,
                                  },
                                });
                              }}
                              type="number"
                              label={"Rejected"}
                              min={0}
                              onWheel={(e) => e.target.blur()}
                            />
                          </Box>
                        </Box>
                      </td>
                    </tr>
                    <tr>
                      <td colSpan={4} style={{ padding: 0, border: "none" }}>
                        <Box mt={2} mb={2}>
                          <Typography variant="h6" mb={2}>
                            IQC Parameters
                          </Typography>
                          <table
                            style={{
                              width: "100%",
                              borderCollapse: "collapse",
                            }}
                          >
                            <thead>
                              <tr>
                                <th
                                  style={{
                                    fontWeight: "bold",
                                    padding: 8,
                                    border: "1px solid #eee",
                                    textAlign: "left",
                                  }}
                                >
                                  Parameter
                                </th>
                                <th
                                  style={{
                                    fontWeight: "bold",
                                    padding: 8,
                                    border: "1px solid #eee",
                                    textAlign: "center",
                                  }}
                                >
                                  Pass
                                </th>
                                <th
                                  style={{
                                    fontWeight: "bold",
                                    padding: 8,
                                    border: "1px solid #eee",
                                    textAlign: "center",
                                  }}
                                >
                                  Reworkable
                                </th>
                                <th
                                  style={{
                                    fontWeight: "bold",
                                    padding: 8,
                                    border: "1px solid #eee",
                                    textAlign: "center",
                                  }}
                                >
                                  Rejected
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(
                                fields?.iqc?.parameters || {},
                              ).map(([name, values]) => (
                                <tr key={name}>
                                  <td
                                    style={{
                                      padding: 8,
                                      border: "1px solid #eee",
                                    }}
                                  >
                                    <Typography>{name}</Typography>
                                  </td>
                                  <td
                                    style={{
                                      padding: 8,
                                      border: "1px solid #eee",
                                      textAlign: "center",
                                    }}
                                  >
                                    <CustomInput
                                      disabled={loading}
                                      value={values?.pass ?? 0}
                                      onChange={(e) => {
                                        const val = Number(e.target.value) || 0;
                                        const rowOtherTotal =
                                          (Number(values?.reworkable) || 0) +
                                          (Number(values?.rejected) || 0);
                                        const maxAllowed = Math.max(
                                          Number(fields?.quantity || 0) -
                                            rowOtherTotal,
                                          0,
                                        );
                                        const nextVal = Math.min(
                                          val,
                                          maxAllowed,
                                        );
                                        const nextParams = {
                                          ...fields.iqc?.parameters,
                                          [name]: {
                                            ...values,
                                            pass: nextVal,
                                          },
                                        };
                                        updateParamsWithTotals(nextParams);
                                      }}
                                      type="number"
                                      onWheel={(e) => e.target.blur()}
                                    />
                                  </td>
                                  <td
                                    style={{
                                      padding: 8,
                                      border: "1px solid #eee",
                                      textAlign: "center",
                                    }}
                                  >
                                    <CustomInput
                                      disabled={loading}
                                      value={values?.reworkable ?? 0}
                                      onChange={(e) => {
                                        const val = Number(e.target.value) || 0;
                                        const rowOtherTotal =
                                          (Number(values?.pass) || 0) +
                                          (Number(values?.rejected) || 0);
                                        const maxAllowed = Math.max(
                                          Number(fields?.quantity || 0) -
                                            rowOtherTotal,
                                          0,
                                        );
                                        const nextVal = Math.min(
                                          val,
                                          maxAllowed,
                                        );
                                        const nextParams = {
                                          ...fields.iqc?.parameters,
                                          [name]: {
                                            ...values,
                                            reworkable: nextVal,
                                          },
                                        };
                                        updateParamsWithTotals(nextParams);
                                      }}
                                      type="number"
                                      onWheel={(e) => e.target.blur()}
                                    />
                                  </td>
                                  <td
                                    style={{
                                      padding: 8,
                                      border: "1px solid #eee",
                                      textAlign: "center",
                                    }}
                                  >
                                    <CustomInput
                                      disabled={loading}
                                      value={values?.rejected ?? 0}
                                      onChange={(e) => {
                                        const val = Number(e.target.value) || 0;
                                        const rowOtherTotal =
                                          (Number(values?.pass) || 0) +
                                          (Number(values?.reworkable) || 0);
                                        const maxAllowed = Math.max(
                                          Number(fields?.quantity || 0) -
                                            rowOtherTotal,
                                          0,
                                        );
                                        const nextVal = Math.min(
                                          val,
                                          maxAllowed,
                                        );
                                        const nextParams = {
                                          ...fields.iqc?.parameters,
                                          [name]: {
                                            ...values,
                                            rejected: nextVal,
                                          },
                                        };
                                        updateParamsWithTotals(nextParams);
                                      }}
                                      type="number"
                                      onWheel={(e) => e.target.blur()}
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </Box>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </Box>
            </>
          )}
        </>
      )}
    </CustomDialog>
  );
};

export default InventoryLogsCreateui;

// {
//   fields?.materialType == 1 && (
//     <AsyncDropDown
//       defaultVal={
//         fields?.materialIds
//           ? {
//             _id: fields?.materialIds?._id,
//             name: fields?.materialIds?.name,
//           }
//           : null
//       }
//       lazyFun={async (para) =>
//         await fetchRawMaterialApi({ ...para, allStatus: true })
//       }
//       OptionComponent={({ option, ...rest }) => {
//         return <ListItem {...rest}>{option?.name}</ListItem>;
//       }}
//       value={fields?.materialId}
//       onChange={async (changedVal) => {
//         if (changedVal) {
//           try {
//             const materialDetails =
//               await fetchMaterialDetailsById(changedVal._id);
//             const pricePerUnit = materialDetails?.price_per_unit;
//             const quantity = Number(fields?.quantity) || 0;
//             setFields((prevFields) => ({
//               ...prevFields,
//               materialId: changedVal._id,
//               itemCode: materialDetails?.code || "",
//               pricePerUnits: pricePerUnit,
//               amount: pricePerUnit * quantity,
//             }));
//           } catch (error) {
//             console.error(
//               "Error fetching material details:",
//               error
//             );
//           }
//         } else {
//           setFields((prevFields) => ({
//             ...prevFields,
//             materialId: null,
//             itemCode: "",
//             pricePerUnits: 0,
//             amount: 0,
//           }));
//         }
//       }}
//       disabled={loading || id}
//       titleKey={"name"}
//       valueKey={"_id"}
//       InputComponent={(params) => (
//         <StyledSearchBar
//           placeholder={"Select Raw Material*"}
//           {...params}
//           margin="none"
//         />
//       )}
//     />
//   )
// }

// {
//   fields?.materialType == 2 && (
//     <AsyncDropDown
//       disabled={loading || id}
//       defaultVal={
//         fields?.materialIds
//           ? {
//             _id: fields?.materialIds?._id,
//             name: fields?.materialIds?.name,
//           }
//           : null
//       }
//       lazyFun={async (para) =>
//         await FetchSKDApi({ ...para, allStatus: true })
//       }
//       OptionComponent={({ option, ...rest }) => {
//         return <ListItem {...rest}>{option?.name}</ListItem>;
//       }}
//       value={fields?.materialId}
//       onChange={async (changedVal) => {
//         if (changedVal) {
//           try {
//             const materialDetails =
//               await fetchMaterialDetailsById(changedVal._id);
//             const pricePerUnit = materialDetails?.price_per_unit;
//             const quantity = Number(fields?.quantity) || 0;
//             setFields((prevFields) => ({
//               ...prevFields,
//               materialId: changedVal._id,
//               itemCode: materialDetails?.code || "",
//               pricePerUnits: pricePerUnit,
//               amount: pricePerUnit * quantity,
//             }));
//           } catch (error) {
//             console.error(
//               "Error fetching material details:",
//               error
//             );
//           }
//         } else {
//           setFields((prevFields) => ({
//             ...prevFields,
//             materialId: null,
//             itemCode: "",
//             pricePerUnits: 0,
//             amount: 0,
//           }));
//         }
//       }}
//       titleKey={"name"}
//       valueKey={"_id"}
//       InputComponent={(params) => (
//         <StyledSearchBar
//           placeholder={"Select Semi Knocked Down (SKD)*"}
//           {...params}
//           margin="none"
//         />
//       )}
//     />
//   )
// }

// {
//   fields?.materialType == 3 && (
//     <AsyncDropDown
//       disabled={loading || id}
//       defaultVal={
//         fields?.materialIds
//           ? {
//             _id: fields?.materialIds?._id,
//             name: fields?.materialIds?.name,
//           }
//           : null
//       }
//       lazyFun={async (para) =>
//         await FetchSFGApi({ ...para, allStatus: true })
//       }
//       OptionComponent={({ option, ...rest }) => {
//         return <ListItem {...rest}>{option?.name}</ListItem>;
//       }}
//       value={fields?.materialId}
//       onChange={async (changedVal) => {
//         if (changedVal) {
//           try {
//             const materialDetails =
//               await fetchMaterialDetailsById(changedVal._id);
//             const pricePerUnit = materialDetails?.price_per_unit;
//             const quantity = Number(fields?.quantity) || 0;
//             setFields((prevFields) => ({
//               ...prevFields,
//               materialId: changedVal._id,
//               itemCode: materialDetails?.code || "",
//               pricePerUnits: pricePerUnit,
//               amount: pricePerUnit * quantity,
//             }));
//           } catch (error) {
//             console.error(
//               "Error fetching material details:",
//               error
//             );
//           }
//         } else {
//           setFields((prevFields) => ({
//             ...prevFields,
//             materialId: null,
//             itemCode: "",
//             pricePerUnits: 0,
//             amount: 0,
//           }));
//         }
//       }}
//       titleKey={"name"}
//       valueKey={"_id"}
//       InputComponent={(params) => (
//         <StyledSearchBar
//           placeholder={"Select Semi-Finished Goods (SFG)*"}
//           {...params}
//           margin="none"
//         />
//       )}
//     />
//   )
// }

// {
//   fields?.materialType == 4 && (
//     <AsyncDropDown
//       disabled={loading || id}
//       defaultVal={
//         fields?.materialIds
//           ? {
//             _id: fields?.materialIds?._id,
//             name: fields?.materialIds?.name,
//           }
//           : null
//       }
//       lazyFun={async (para) =>
//         await FetchFGApi({ ...para, allStatus: true })
//       }
//       OptionComponent={({ option, ...rest }) => {
//         return <ListItem {...rest}>{option?.name}</ListItem>;
//       }}
//       value={fields?.materialId}
//       onChange={async (changedVal) => {
//         if (changedVal) {
//           try {
//             const materialDetails =
//               await fetchMaterialDetailsById(changedVal._id);
//             const pricePerUnit = materialDetails?.price_per_unit;
//             const quantity = Number(fields?.quantity) || 0;
//             setFields((prevFields) => ({
//               ...prevFields,
//               materialId: changedVal._id,
//               itemCode: materialDetails?.code || "",
//               pricePerUnits: pricePerUnit,
//               amount: pricePerUnit * quantity,
//             }));
//           } catch (error) {
//             console.error(
//               "Error fetching material details:",
//               error
//             );
//           }
//         } else {
//           setFields((prevFields) => ({
//             ...prevFields,
//             materialId: null,
//             itemCode: "",
//             pricePerUnits: 0,
//             amount: 0,
//           }));
//         }
//       }}
//       titleKey={"name"}
//       valueKey={"_id"}
//       InputComponent={(params) => (
//         <StyledSearchBar
//           placeholder={"Select Finished Goods (FG)*"}
//           {...params}
//           margin="none"
//         />
//       )}
//     />
//   )
// }
